import { Fragment, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import SoftButton from "components/SoftButton";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { GeneralJournalService } from "services/general-journal";
import { formatDate, formatVndIntegerValue } from "utils";

function getPrintTemplate(data) {
  const accountCodes = data?.accountCodes || [];
  const rows = data?.rows || [];
  const summary = data?.summary || {};

  const headRow1 = accountCodes
    .map((code) => `<th colspan="2" style="border:1px solid #000;padding:6px;">TK ${code}</th>`)
    .join("");
  const headRow2 = accountCodes
    .map(
      () =>
        `<th style="border:1px solid #000;padding:6px;">Nợ</th><th style="border:1px solid #000;padding:6px;">Có</th>`
    )
    .join("");

  const bodyRows = rows
    .map((row) => {
      const entries = accountCodes
        .map((code) => {
          const entry = row?.accountEntries?.[code] || { debit: 0, credit: 0 };
          return `<td style="border:1px solid #000;padding:6px;text-align:right;">${
            entry.debit ? formatVndIntegerValue(entry.debit) : ""
          }</td><td style="border:1px solid #000;padding:6px;text-align:right;">${
            entry.credit ? formatVndIntegerValue(entry.credit) : ""
          }</td>`;
        })
        .join("");
      return `<tr>
        <td style="border:1px solid #000;padding:6px;text-align:center;">${formatDate(
          row.recordDate
        )}</td>
        <td style="border:1px solid #000;padding:6px;text-align:center;">${row.voucherNumber || ""}</td>
        <td style="border:1px solid #000;padding:6px;text-align:center;">${formatDate(
          row.voucherDate
        )}</td>
        <td style="border:1px solid #000;padding:6px;">${row.description || ""}</td>
        <td style="border:1px solid #000;padding:6px;text-align:right;">${formatVndIntegerValue(
          row.amount || 0
        )}</td>
        <td style="border:1px solid #000;padding:6px;text-align:center;">${row.debitAccount || ""}</td>
        <td style="border:1px solid #000;padding:6px;text-align:center;">${row.creditAccount || ""}</td>
        <td style="border:1px solid #000;padding:6px;text-align:center;">${row.lineNumber || ""}</td>
        ${entries}
      </tr>`;
    })
    .join("");

  const summaryEntries = accountCodes
    .map((code) => {
      const entry = summary?.accountTotals?.[code] || { debit: 0, credit: 0 };
      return `<td style="border:1px solid #000;padding:6px;text-align:right;font-weight:700;">${formatVndIntegerValue(
        entry.debit || 0
      )}</td><td style="border:1px solid #000;padding:6px;text-align:right;font-weight:700;">${formatVndIntegerValue(
        entry.credit || 0
      )}</td>`;
    })
    .join("");

  return `<!doctype html>
<html>
<head>
  <meta charset="utf-8" />
  <title>S01-DN</title>
  <style>
    body { font-family: "Times New Roman", serif; margin: 16px; font-size: 12px; color: #000; }
    .title { text-align:center; font-weight:700; font-size:20px; margin-bottom: 12px; }
    .meta { display:flex; justify-content:space-between; margin-bottom:8px; }
    .meta-col { line-height: 1.4; }
    table { width:100%; border-collapse: collapse; }
    th, td { border:1px solid #000; padding:6px; }
    .notes { margin-top: 8px; line-height: 1.8; }
    .sign { margin-top: 24px; }
    .sign-date-row { display:flex; justify-content:flex-end; margin-bottom: 4px; }
    .sign-date { width: 32%; text-align:center; }
    .sign-row { display:flex; justify-content:space-between; gap:16px; }
    .sign-item { width: 32%; text-align:center; }
    .sign-role { font-weight:700; }
    .sign-name { font-style: italic; }
  </style>
</head>
<body>
  <div class="meta">
    <div class="meta-col"><strong>Đơn vị:</strong> ....................<br/><strong>Địa chỉ:</strong> ....................</div>
    <div class="meta-col" style="text-align:right;"><strong>Mẫu số S01-DN</strong><br/>(Kèm theo Thông tư số 99/2025/TT-BTC)</div>
  </div>
  <div class="title">NHẬT KÝ - SỔ CÁI (S01-DN)</div>
  <div style="text-align:center; margin-bottom: 8px;"><strong>Năm:</strong> ${data?.year || ""}</div>
  <table>
    <thead>
      <tr>
        <th rowspan="2">Ngày tháng ghi sổ</th>
        <th colspan="2">Chứng từ</th>
        <th rowspan="2">Diễn giải</th>
        <th rowspan="2">Số tiền phát sinh</th>
        <th rowspan="2">TK Nợ</th>
        <th rowspan="2">TK Có</th>
        <th rowspan="2">Thứ tự dòng</th>
        ${headRow1}
      </tr>
      <tr>
        <th>Số hiệu</th>
        <th>Ngày tháng</th>
        ${headRow2}
      </tr>
    </thead>
    <tbody>
      ${bodyRows}
      <tr>
        <td colspan="4" style="text-align:center;font-weight:700;">Cộng số phát sinh</td>
        <td style="text-align:right;font-weight:700;">${formatVndIntegerValue(
          summary?.totalJournalAmount || 0
        )}</td>
        <td></td>
        <td></td>
        <td></td>
        ${summaryEntries}
      </tr>
    </tbody>
  </table>
  <div class="notes">
    <div>- Sổ này có .... trang, đánh số từ trang số 01 đến trang ...</div>
    <div>- Ngày mở sổ:....</div>
  </div>
  <div class="sign">
    <div class="sign-date-row">
      <div class="sign-date">Ngày..... tháng.... năm.....</div>
    </div>
    <div class="sign-row">
      <div class="sign-item">
        <div class="sign-role">Người ghi sổ</div>
        <div class="sign-name">(Ký, họ tên)</div>
      </div>
      <div class="sign-item">
        <div class="sign-role">Kế toán trưởng</div>
        <div class="sign-name">(Ký, họ tên)</div>
      </div>
      <div class="sign-item">
        <div class="sign-role">Người đại diện theo pháp luật</div>
        <div class="sign-name">(Ký, họ tên, đóng dấu)</div>
      </div>
    </div>
  </div>
</body>
</html>`;
}

export default function GeneralJournalLedgerDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  useEffect(() => {
    if (!id) return;
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await GeneralJournalService.getS01DnRecordById(id);
        const payload = response?.data || response || {};
        setData(payload);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const accountCodes = useMemo(() => data?.accountCodes || [], [data]);
  const rows = data?.rows || [];
  const summary = data?.summary || {};

  const handlePrint = () => {
    const html = getPrintTemplate(data || {});
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 150);
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <SoftBox py={3}>
        <Grid container justifyContent="center">
          <Grid item xs={12}>
            <Card>
              <SoftBox p={3}>
                <SoftTypography variant="h5" fontWeight="bold" mb={2}>
                  Chi tiết S01-DN
                </SoftTypography>
                {loading ? (
                  <SoftTypography variant="button">Đang tải dữ liệu...</SoftTypography>
                ) : (
                  <>
                    <SoftBox mb={2}>
                      <SoftTypography variant="button">
                        Năm: <strong>{data?.year || ""}</strong>
                      </SoftTypography>
                      <br />
                      <SoftTypography variant="button">
                        Kỳ: {formatDate(data?.fromDate)} - {formatDate(data?.toDate)}
                      </SoftTypography>
                    </SoftBox>
                    <SoftBox sx={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr>
                            <th rowSpan={2} style={thStyle}>
                              Ngày tháng ghi sổ
                            </th>
                            <th colSpan={2} style={thStyle}>
                              Chứng từ
                            </th>
                            <th rowSpan={2} style={thStyle}>
                              Diễn giải
                            </th>
                            <th rowSpan={2} style={thStyle}>
                              Số tiền phát sinh
                            </th>
                            <th rowSpan={2} style={thStyle}>
                              TK Nợ
                            </th>
                            <th rowSpan={2} style={thStyle}>
                              TK Có
                            </th>
                            <th rowSpan={2} style={thStyle}>
                              Thứ tự dòng
                            </th>
                            {accountCodes.map((code) => (
                              <th key={code} colSpan={2} style={thStyle}>
                                TK {code}
                              </th>
                            ))}
                          </tr>
                          <tr>
                            <th style={thStyle}>Số hiệu</th>
                            <th style={thStyle}>Ngày tháng</th>
                            {accountCodes.map((code) => (
                              <Fragment key={code}>
                                <th style={thStyle}>Nợ</th>
                                <th style={thStyle}>Có</th>
                              </Fragment>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {rows.map((row, index) => (
                            <tr key={`${row.lineNumber}-${index}`}>
                              <td style={tdCenterStyle}>{formatDate(row.recordDate)}</td>
                              <td style={tdCenterStyle}>{row.voucherNumber || ""}</td>
                              <td style={tdCenterStyle}>{formatDate(row.voucherDate)}</td>
                              <td style={tdLeftStyle}>{row.description || ""}</td>
                              <td style={tdRightStyle}>{formatVndIntegerValue(row.amount || 0)}</td>
                              <td style={tdCenterStyle}>{row.debitAccount || ""}</td>
                              <td style={tdCenterStyle}>{row.creditAccount || ""}</td>
                              <td style={tdCenterStyle}>{row.lineNumber || ""}</td>
                              {accountCodes.map((code) => {
                                const entry = row.accountEntries?.[code] || { debit: 0, credit: 0 };
                                return (
                                  <Fragment key={`${row.lineNumber}-${code}`}>
                                    <td style={tdRightStyle}>
                                      {entry.debit ? formatVndIntegerValue(entry.debit) : ""}
                                    </td>
                                    <td style={tdRightStyle}>
                                      {entry.credit ? formatVndIntegerValue(entry.credit) : ""}
                                    </td>
                                  </Fragment>
                                );
                              })}
                            </tr>
                          ))}
                          <tr>
                            <td colSpan={4} style={{ ...tdCenterStyle, fontWeight: 700 }}>
                              Cộng số phát sinh
                            </td>
                            <td style={{ ...tdRightStyle, fontWeight: 700 }}>
                              {formatVndIntegerValue(summary?.totalJournalAmount || 0)}
                            </td>
                            <td style={tdCenterStyle} />
                            <td style={tdCenterStyle} />
                            <td style={tdCenterStyle} />
                            {accountCodes.map((code) => (
                              <Fragment key={`sum-${code}`}>
                                <td style={{ ...tdRightStyle, fontWeight: 700 }}>
                                  {formatVndIntegerValue(summary?.accountTotals?.[code]?.debit || 0)}
                                </td>
                                <td style={{ ...tdRightStyle, fontWeight: 700 }}>
                                  {formatVndIntegerValue(summary?.accountTotals?.[code]?.credit || 0)}
                                </td>
                              </Fragment>
                            ))}
                          </tr>
                        </tbody>
                      </table>
                    </SoftBox>
                  </>
                )}
                <SoftBox mt={3} display="flex" justifyContent="space-between">
                  <SoftButton variant="gradient" color="light" onClick={() => navigate(-1)}>
                    Trở về
                  </SoftButton>
                  <SoftButton variant="gradient" color="success" onClick={handlePrint} type="button">
                    IN
                  </SoftButton>
                </SoftBox>
              </SoftBox>
            </Card>
          </Grid>
        </Grid>
      </SoftBox>
    </DashboardLayout>
  );
}

const thStyle = {
  border: "1px solid #111",
  padding: "6px",
  textAlign: "center",
  verticalAlign: "middle",
  fontSize: "12px",
  fontWeight: 700,
  whiteSpace: "nowrap",
};
const tdBaseStyle = {
  border: "1px solid #111",
  padding: "6px",
  fontSize: "12px",
  verticalAlign: "top",
};
const tdCenterStyle = { ...tdBaseStyle, textAlign: "center" };
const tdLeftStyle = { ...tdBaseStyle, textAlign: "left", minWidth: "260px" };
const tdRightStyle = { ...tdBaseStyle, textAlign: "right" };
